import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTop — resets the window scroll position on every route change.
 *
 * React Router preserves the previous scroll position by default, which is
 * confusing when a user navigates from mid/bottom of one page (e.g. footer
 * links like "Chinchwad", "Contact") to a different route — they land at the
 * bottom of the new page and can't tell the navigation happened.
 *
 * Behavior:
 *   - Runs on every `pathname` change (search-param changes stay put so
 *     pagination/filter interactions on the same page don't jump).
 *   - Respects `state.preserveScroll` so specific navigations can opt out
 *     (e.g. tab switches inside a dashboard).
 *   - Uses `instant` scrolling to avoid a jarring smooth-scroll animation on
 *     top-level navigation; the visitor arriving on a fresh page shouldn't
 *     see the old page scroll upward first.
 */
export default function ScrollToTop() {
  const { pathname, state } = useLocation();

  useEffect(() => {
    if (state && state.preserveScroll) return;
    // `behavior: 'instant'` is well-supported in modern browsers; fall back to
    // the two-argument form just in case.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname, state]);

  return null;
}
